import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import frameBg from "../../../../Assets/FrameBGDashboard.png";
import NotificationAlert from "../../../Alerts/NotificationAlert";
import {
  createProject,
  editProject,
} from "../../AdminServices/AdminProjectService";
import {
  getProjectTypes,
  getProjectStatus,
  getTechStack,
} from "../../AdminServices/AdminMetadataService";

const CreateProjectModal = ({ show, onClose, onCreated, editData }) => {
  const initialForm = {
    projectName: "",
    projectDescription: "",
    projectType: "",
    currentStatus: "",
    techStack: [],
    environments: [
      {
        environmentType: "",
        loginType: 1,
        appURL: "",
        username: "",
        password: "",
      },
    ],
    ipAddress: "",
    dbDetails: "",
    gitRepoPath: "",
    logo: null,
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectStatus, setProjectStatus] = useState([]);
  const [techStackList, setTechStackList] = useState([]);
  const [showTechDropdown, setShowTechDropdown] = useState(false);
  const navigate = useNavigate();

  // Reset form each time modal opens
  useEffect(() => {
    if (show) {
      loadMetaData();
    }
  }, [show]);

  useEffect(() => {
    if (show && editData && techStackList.length > 0) {
      setForm({
        projectName: editData.projectName || "",
        projectDescription: editData.projectDescription || "",
        projectType: (() => {
          const match = projectTypes.find(
            (t) => t.name === editData.projectType,
          );
          return match ? String(match.id) : "";
        })(),

        currentStatus: (() => {
          const match = projectStatus.find(
            (s) => s.name === editData.currentStatus,
          );
          return match ? String(match.id) : "";
        })(),
        techStack: editData.techStack
          ? editData.techStack
              .split(",")
              .map((name) => {
                const match = techStackList.find(
                  (t) => t.name.trim() === name.trim(),
                );
                return match ? String(match.id) : null;
              })
              .filter(Boolean)
          : [],
        environments:
          editData.environments && editData.environments.length > 0
            ? editData.environments.map((env) => ({
                environmentUID: env.environmentUID || null,
                environmentType: env.environmentType || "",
                loginType: Number(env.loginType) || 1,
                appURL: env.appURL || "",
                username: env.username || "",
                password: env.password || "",
              }))
            : [
                {
                  environmentUID: null,
                  environmentType: "",
                  loginType: 1,
                  appURL: "",
                  username: "",
                  password: "",
                },
              ],
        ipAddress: editData.ipAddress || "",
        dbDetails: editData.dbDetails || "",
        gitRepoPath: editData.gitRepoMethod  || "",
        logo: null,
        projectUID: editData.projectUID,
      });
    }

    if (show && !editData) {
      setForm(initialForm);
    }

    setErrors({});
    setAlert(null);
  }, [show, editData, techStackList, projectTypes, projectStatus]);

  const loadMetaData = async () => {
    try {
      const [typeRes, statusRes, techRes] = await Promise.all([
        getProjectTypes(),
        getProjectStatus(),
        getTechStack(),
      ]);

      setProjectTypes(typeRes.data?.data || []);
      setProjectStatus(statusRes.data?.data || []);
      setTechStackList(techRes.data?.data || []);
    } catch (err) {
      console.error("Error loading metadata", err);
    }
  };

  if (!show) return null;

  // Handle input change
  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Validation
  const validate = () => {
    let temp = {};

    // Project Name
    if (!form.projectName.trim()) {
      temp.projectName = "Project name is required.";
    } else if (form.projectName.length < 3) {
      temp.projectName = "Minimum 3 characters required.";
    }

    // Description
    if (!form.projectDescription.trim()) {
      temp.projectDescription = "Project description is required.";
    } else if (form.projectDescription.length < 10) {
      temp.projectDescription = "Minimum 10 characters required.";
    }

    // Project Type
    if (!form.projectType) {
      temp.projectType = "Project type is required.";
    }

    // Status
    if (!form.currentStatus) {
      temp.currentStatus = "Status is required.";
    }
    // Environment Validation
    form.environments.forEach((env, index) => {
      // Non-CLS Validation
      if (env.loginType === 2) {
        if (!env.username.trim()) {
          temp[`username_${index}`] = "Username / Email is required.";
        }

        if (!env.password.trim()) {
          temp[`password_${index}`] = "Password is required.";
        }
      }
    });
    return temp;
  };

  const addProjectURL = () => {
    setForm((prev) => ({
      ...prev,
      environments: [
        ...prev.environments,
        {
          environmentType: "",
          loginType: 1,
          appURL: "",
          username: "",
          password: "",
        },
      ],
    }));
  };

  const removeProjectURL = (index) => {
    const updated = [...form.environments];
    updated.splice(index, 1);

    setForm((prev) => ({
      ...prev,
      environments: updated,
    }));
  };

  const handleURLChange = (index, field, value) => {
    const updated = [...form.environments];

    updated[index][field] = value;

    // Clear username/password if CLS selected
    if (field === "loginType" && value === 1) {
      updated[index].username = "";
      updated[index].password = "";
    }

    setForm((prev) => ({
      ...prev,
      environments: updated,
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    const validation = validate();

    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("ProjectName", form.projectName);
      formData.append("ProjectDescription", form.projectDescription);
      formData.append("ProjectType", form.projectType);
      formData.append("CurrentStatus", form.currentStatus);
      form.environments.forEach((env, index) => {
        // Edit only
        if (editData && env.environmentUID) {
          formData.append(
            `Environments[${index}].EnvironmentUID`,
            env.environmentUID,
          );
        }

        formData.append(
          `Environments[${index}].EnvironmentType`,
          env.environmentType || "",
        );

        formData.append(`Environments[${index}].LoginType`, env.loginType);

        formData.append(`Environments[${index}].AppURL`, env.appURL || "");

        formData.append(`Environments[${index}].Username`, env.username || "");

        formData.append(`Environments[${index}].Password`, env.password || "");
      });
      formData.append("IPAddress", form.ipAddress);
      formData.append("DBDetails", form.dbDetails);
      formData.append("GitRepoPath", form.gitRepoPath);

      // TechStack (multiple)
      const selectedTechNames = techStackList
        .filter((t) => form.techStack.includes(String(t.id)))
        .map((t) => t.name);

      formData.append("TechStack", selectedTechNames.join(", "));

      // Logo
      if (form.logo) {
        formData.append("Logo", form.logo);
      } else {
        formData.append("Logo", "");
      }

      if (editData) {
        formData.append("ProjectUID", form.projectUID);
      }

      const res = editData
        ? await editProject(formData)
        : await createProject(formData);

      if (res.data.status === "Success") {
        setAlert({
          type: "success",
          message: res.data.message || "Success!",
        });
        if (onCreated) onCreated();

        setTimeout(() => {
          setAlert(null);
          onClose();
        }, 1000);
      } else {
        setAlert({ type: "error", message: res.data.message });
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Something went wrong.",
      });
    }

    setLoading(false);
  };

  const handleTechStackChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(
      (opt) => opt.value,
    );

    setForm((prev) => ({
      ...prev,
      techStack: selectedOptions,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setForm((prev) => ({ ...prev, logo: null }));
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    const maxSize = 500 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        logo: "Only JPG, PNG, SVG files are allowed.",
      }));

      e.target.value = null;
      setForm((prev) => ({ ...prev, logo: null }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        logo: "File size must be less than 500 KB.",
      }));

      e.target.value = null;
      setForm((prev) => ({ ...prev, logo: null }));
      return;
    }
    setErrors((prev) => ({ ...prev, logo: "" }));

    setForm((prev) => ({
      ...prev,
      logo: file,
    }));
  };

  const toggleTech = (id) => {
    setForm((prev) => {
      const exists = prev.techStack.includes(id);

      return {
        ...prev,
        techStack: exists
          ? prev.techStack.filter((x) => x !== id)
          : [...prev.techStack, id],
      };
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex justify-end z-50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl h-full shadow-2xl border-l border-gray-200 flex flex-col animate-slideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center px-8 py-6 border-b text-white shadow-md bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${frameBg})` }}
        >
          <h2 className="text-2xl font-semibold">
            {editData ? "Edit Project" : "Create New Project"}
          </h2>
          <button onClick={onClose}>
            <X size={26} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Project Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="projectName"
                value={form.projectName}
                onChange={handleInput}
                disabled={!!editData}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  editData ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
              {errors.projectName && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.projectName}
                </p>
              )}
            </div>

            {/* Project Description */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="projectDescription"
                rows={4}
                value={form.projectDescription}
                onChange={handleInput}
                className="w-full p-2 border rounded-md mt-1 text-sm"
                placeholder="Enter project description"
              />
              {errors.projectDescription && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.projectDescription}
                </p>
              )}
            </div>
            {/* Project Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Project Type <span className="text-red-500">*</span>
              </label>
              <select
                name="projectType"
                value={form.projectType}
                onChange={handleInput}
                className="w-full p-2 border rounded-md mt-1 text-sm"
              >
                <option value="">Select Project Type</option>
                {projectTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.projectType && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.projectType}
                </p>
              )}
            </div>
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="currentStatus"
                value={form.currentStatus}
                onChange={handleInput}
                className="w-full p-2 border rounded-md mt-1 text-sm"
              >
                <option value="">Select Status</option>
                {projectStatus.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.currentStatus && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.currentStatus}
                </p>
              )}
            </div>

            {/* Tech Stack */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700">
                Tech Stack
              </label>

              {/* Input Box */}
              <div
                onClick={() => setShowTechDropdown(!showTechDropdown)}
                className="w-full p-2 border rounded-md mt-1 text-sm cursor-pointer bg-white flex justify-between items-center"
              >
                <span className="text-gray-700">
                  {form.techStack.length > 0
                    ? techStackList
                        .filter((t) => form.techStack.includes(String(t.id)))
                        .map((t) => t.name)
                        .join(", ")
                    : "Select Tech Stack"}
                </span>

                <span>▼</span>
              </div>

              {/* Dropdown */}
              {showTechDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {techStackList.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => toggleTech(String(item.id))}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.techStack.includes(String(item.id))}
                        readOnly
                      />
                      <span className="text-sm">{item.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Git Repository URL
              </label>
              <input
                type="text"
                name="gitRepoPath"
                value={form.gitRepoPath}
                onChange={handleInput}
                className="w-full p-2 border rounded-md mt-1 text-sm"
                placeholder="Enter Git repository URL"
              />
            </div>

            {/* IP Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Server IP
              </label>
              <input
                type="text"
                name="ipAddress"
                value={form.ipAddress}
                onChange={handleInput}
                className="w-full p-2 border rounded-md mt-1 text-sm"
                placeholder="Enter server IP"
              />
            </div>

            {/* DB Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Database Details
              </label>
              <input
                type="text"
                name="dbDetails"
                value={form.dbDetails}
                onChange={handleInput}
                className="w-full p-2 border rounded-md mt-1 text-sm"
                placeholder="Enter database details"
              />
            </div>

            {/* Logo Upload */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Logo
              </label>
              <input
                type="file"
                name="logo"
                accept=".jpg,.jpeg,.png,.svg"
                onChange={handleFileChange}
                className="w-full mt-1 text-sm"
              />
            </div>

            {/* App URL */}
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Application URLs
                </label>

                <button
                  type="button"
                  onClick={addProjectURL}
                  className="px-3 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow hover:opacity-90"
                >
                  + Add URL
                </button>
              </div>

              {form.environments.map((item, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-5 mb-5 bg-white shadow-sm"
                >
                  {/* URL Type */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      URL Type
                    </label>

                    <select
                      value={item.environmentType}
                      onChange={(e) =>
                        handleURLChange(
                          index,
                          "environmentType",
                          e.target.value,
                        )
                      }
                      className="w-full p-2 border border-gray-300 rounded-md mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#00ACED]/30"
                    >
                      <option value="">Select</option>
                      <option value="DEV">DEV</option>
                      <option value="UAT">UAT</option>
                      <option value="PROD">PROD</option>
                    </select>
                  </div>

                  {/* URL */}
                  <div className="mb-3">
                    <label className="text-sm font-medium">
                      Application URL
                    </label>

                    <input
                      type="text"
                      value={item.appURL}
                      onChange={(e) =>
                        handleURLChange(index, "appURL", e.target.value)
                      }
                      className="w-full p-2 border border-gray-300 rounded-md mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#00ACED]/30"
                      placeholder="Enter URL"
                    />
                  </div>

                  {/* Auth Type */}
                  <div className="mb-3">
                    <label className="text-sm font-medium block mb-2">
                      Authentication Type
                    </label>

                    <div className="flex gap-5">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={item.loginType === 1}
                          onChange={() =>
                            handleURLChange(index, "loginType", 1)
                          }
                        />
                        CLS Based
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={item.loginType === 2}
                          onChange={() =>
                            handleURLChange(index, "loginType", 2)
                          }
                        />
                        Non-CLS Based
                      </label>
                    </div>
                  </div>

                  {/* Username & Password */}
                  {item.loginType === 2 && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">
                          Username / Email{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        <input
                          type="text"
                          value={item.username}
                          onChange={(e) =>
                            handleURLChange(index, "username", e.target.value)
                          }
                          className="w-full p-2 border border-gray-300 rounded-md mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#00ACED]/30"
                        />
                        {errors[`username_${index}`] && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors[`username_${index}`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-medium">
                          Password <span className="text-red-500">*</span>
                        </label>

                        <input
                          type="password"
                          value={item.password}
                          onChange={(e) =>
                            handleURLChange(index, "password", e.target.value)
                          }
                          className="w-full p-2 border border-gray-300 rounded-md mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#00ACED]/30"
                        />
                        {errors[`password_${index}`] && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors[`password_${index}`]}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Remove */}
                  <div className="mt-4 text-right">
                    <button
                      type="button"
                      onClick={() => removeProjectURL(index)}
                      className="text-red-500 text-sm"
                    >
                      Remove URL
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="px-8 py-4 border-t bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow hover:opacity-90"
            disabled={loading}
          >
            {loading
              ? editData
                ? "Updating..."
                : "Creating..."
              : editData
                ? "Update Project"
                : "Create Project"}{" "}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default CreateProjectModal;
